import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Clock, 
  Euro, 
  Star, 
  TrendingUp, 
  TrendingDown,
  CheckCircle2, 
  AlertCircle,
  FileText,
  Calendar,
  ChevronDown,
  ChevronRight,
  Plus,
  Minus,
  Send,
  Loader2,
  User,
  Building
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const MemberActivityReport = ({ memberId, contractId, onReportValidated }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [expandedLines, setExpandedLines] = useState({});
  const [validating, setValidating] = useState(false);
  
  // Validation form state
  const [validationForm, setValidationForm] = useState({
    global_bonus: 0,
    global_bonus_type: 'fixed', // 'fixed' or 'percentage'
    global_bonus_reason: '',
    agency_notes: '',
    agency_evaluation: ''
  });

  useEffect(() => {
    fetchReports();
  }, [memberId, contractId]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (memberId) params.member_id = memberId;
      if (contractId) params.contract_id = contractId;
      
      const res = await api.get('/monthly-reports/', { params });
      setReports(res.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (month) => {
    if (!contractId) {
      toast.error('Aucun contrat actif pour ce membre');
      return;
    }
    
    try {
      const res = await api.post('/monthly-reports/generate', {
        contract_id: contractId,
        month: month
      });
      toast.success(res.data.message);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  const handleValidate = async () => {
    if (!selectedReport) return;
    
    setValidating(true);
    try {
      await api.put(`/monthly-reports/${selectedReport.id}/validate`, {
        global_bonus: parseFloat(validationForm.global_bonus) || 0,
        global_bonus_type: validationForm.global_bonus_type,
        global_bonus_reason: validationForm.global_bonus_reason,
        agency_notes: validationForm.agency_notes,
        agency_evaluation: validationForm.agency_evaluation,
        task_adjustments: []
      });
      
      toast.success('Rapport validé avec succès');
      setShowValidationModal(false);
      fetchReports();
      if (onReportValidated) onReportValidated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const toggleLine = (lineId) => {
    setExpandedLines(prev => ({
      ...prev,
      [lineId]: !prev[lineId]
    }));
  };

  const openValidationModal = (report) => {
    setSelectedReport(report);
    setValidationForm({
      global_bonus: report.global_bonus || 0,
      global_bonus_type: report.global_bonus_type || 'fixed',
      global_bonus_reason: report.global_bonus_reason || '',
      agency_notes: report.agency_notes || '',
      agency_evaluation: report.agency_evaluation || ''
    });
    setShowValidationModal(true);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get current month for quick generation
  const currentMonth = new Date().toISOString().slice(0, 7);
  const lastMonth = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 7);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with generate buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Rapports d'activité</h3>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => generateReport(lastMonth)}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Mois précédent
          </Button>
          <Button 
            size="sm"
            onClick={() => generateReport(currentMonth)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Mois en cours
          </Button>
        </div>
      </div>

      {/* Reports list */}
      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Aucun rapport d'activité</p>
            <p className="text-sm mt-1">Générez un rapport pour voir l'activité du membre</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className={cn(
              "transition-all",
              report.status === 'validated' && "border-emerald-200 bg-emerald-50/30",
              report.status === 'draft' && "border-amber-200 bg-amber-50/30"
            )}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg">{report.month_label}</CardTitle>
                    <Badge variant={report.status === 'validated' ? 'default' : 'outline'} className={cn(
                      report.status === 'validated' && "bg-emerald-500",
                      report.status === 'draft' && "border-amber-400 text-amber-700"
                    )}>
                      {report.status === 'validated' ? 'Validé' : 'Brouillon'}
                    </Badge>
                  </div>
                  
                  {report.status === 'draft' && (
                    <Button size="sm" onClick={() => openValidationModal(report)}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Valider le rapport
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 p-3 bg-white/50 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-800">{report.total_hours}h</div>
                    <div className="text-xs text-slate-500">Heures totales</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-800">{report.hourly_rate}€</div>
                    <div className="text-xs text-slate-500">Taux horaire</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-800">{report.base_amount?.toFixed(2)}€</div>
                    <div className="text-xs text-slate-500">Montant de base</div>
                  </div>
                  <div className="text-center">
                    <div className={cn(
                      "text-2xl font-bold",
                      report.total_bonus_malus > 0 && "text-emerald-600",
                      report.total_bonus_malus < 0 && "text-red-600",
                      report.total_bonus_malus === 0 && "text-slate-400"
                    )}>
                      {report.total_bonus_malus > 0 ? '+' : ''}{report.total_bonus_malus?.toFixed(2)}€
                    </div>
                    <div className="text-xs text-slate-500">Bonus/Malus</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{report.final_amount?.toFixed(2)}€</div>
                    <div className="text-xs text-slate-500">Montant final</div>
                  </div>
                </div>

                {/* Efficiency indicator */}
                {report.efficiency_percentage && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-white/50 rounded-lg">
                    {report.efficiency_percentage >= 100 ? (
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-orange-500" />
                    )}
                    <span className="text-sm">
                      Efficacité: <strong>{report.efficiency_percentage}%</strong>
                      {report.total_gain !== 0 && (
                        <span className={cn("ml-2", report.total_gain > 0 ? "text-emerald-600" : "text-red-600")}>
                          ({report.total_gain > 0 ? '+' : ''}{report.total_gain?.toFixed(2)}€)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {/* Client satisfaction */}
                {report.client_satisfaction_avg && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-white/50 rounded-lg">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    <span className="text-sm">
                      Satisfaction client moyenne: <strong>{report.client_satisfaction_avg}/5</strong>
                      <span className="text-slate-500 ml-2">({report.client_feedback_count} évaluations)</span>
                    </span>
                  </div>
                )}

                {/* Activity lines table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2 font-medium">Tâche</th>
                        <th className="text-left p-2 font-medium">Client</th>
                        <th className="text-center p-2 font-medium">Chrono</th>
                        <th className="text-center p-2 font-medium">Budget</th>
                        <th className="text-center p-2 font-medium">Gain</th>
                        <th className="text-center p-2 font-medium">Éval.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.activity_lines || report.tasks || []).map((line, idx) => (
                        <React.Fragment key={line.task_id || idx}>
                          <tr 
                            className={cn(
                              "border-t hover:bg-slate-50 cursor-pointer transition-colors",
                              expandedLines[line.task_id || idx] && "bg-slate-50"
                            )}
                            onClick={() => toggleLine(line.task_id || idx)}
                          >
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                {expandedLines[line.task_id || idx] ? (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                                <div>
                                  <div className="font-medium truncate max-w-[200px]">{line.task_name}</div>
                                  {line.project_name && (
                                    <div className="text-xs text-slate-500">{line.project_name}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-2">
                              {line.client_name ? (
                                <div className="flex items-center gap-1">
                                  <Building className="w-3 h-3 text-slate-400" />
                                  <span className="truncate max-w-[120px]">{line.client_name}</span>
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span className="font-mono">{formatDuration(line.consumed_minutes || 0)}</span>
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <span className="font-mono text-slate-500">
                                {line.budget_minutes > 0 ? formatDuration(line.budget_minutes) : '-'}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              {line.task_gain !== 0 && line.task_gain !== undefined ? (
                                <span className={cn(
                                  "font-mono font-medium",
                                  line.task_gain > 0 ? "text-emerald-600" : "text-red-600"
                                )}>
                                  {line.task_gain > 0 ? '+' : ''}{line.task_gain?.toFixed(0)}€
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {line.client_rating ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                  <span>{line.client_rating}/5</span>
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                          
                          {/* Expanded details */}
                          {expandedLines[line.task_id || idx] && (
                            <tr className="bg-slate-50 border-t border-slate-100">
                              <td colSpan={6} className="p-3">
                                <div className="space-y-2 text-xs">
                                  {/* Time entries */}
                                  {line.time_entries && line.time_entries.length > 0 && (
                                    <div>
                                      <div className="font-medium text-slate-600 mb-1">Détail des chronos:</div>
                                      <div className="space-y-1">
                                        {line.time_entries.map((te, teIdx) => (
                                          <div key={teIdx} className="flex items-center gap-2 text-slate-500">
                                            <span>{formatDate(te.start_time)}</span>
                                            <span>•</span>
                                            <span className="font-mono">{formatDuration(te.duration_minutes || 0)}</span>
                                            {te.notes && <span className="truncate">- {te.notes}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Client feedback */}
                                  {line.client_feedback && (
                                    <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                                      <div className="font-medium text-amber-700 mb-1">Feedback client:</div>
                                      <p className="text-amber-900">{line.client_feedback}</p>
                                    </div>
                                  )}
                                  
                                  {/* Bonus/Malus if any */}
                                  {line.bonus_malus !== 0 && (
                                    <div className={cn(
                                      "mt-2 p-2 rounded border",
                                      line.bonus_malus > 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                                    )}>
                                      <div className="font-medium">
                                        {line.bonus_malus > 0 ? 'Bonus' : 'Malus'}: {line.bonus_malus > 0 ? '+' : ''}{line.bonus_malus}€
                                      </div>
                                      {line.bonus_malus_reason && (
                                        <p className="text-slate-600">{line.bonus_malus_reason}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Agency notes if validated */}
                {report.status === 'validated' && report.agency_notes && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="font-medium text-blue-800 mb-1">Notes de validation:</div>
                    <p className="text-blue-900 text-sm">{report.agency_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Validation Modal */}
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Valider le rapport - {selectedReport?.month_label}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Heures travaillées: <strong>{selectedReport?.total_hours}h</strong></div>
                <div>Taux horaire: <strong>{selectedReport?.hourly_rate}€/h</strong></div>
                <div>Montant de base: <strong>{selectedReport?.base_amount?.toFixed(2)}€</strong></div>
                {selectedReport?.client_satisfaction_avg && (
                  <div>Satisfaction: <strong>{selectedReport?.client_satisfaction_avg}/5</strong></div>
                )}
              </div>
            </div>

            {/* Bonus/Malus */}
            <div className="space-y-3">
              <Label>Bonus / Malus global</Label>
              <div className="flex gap-2">
                <Select 
                  value={validationForm.global_bonus_type}
                  onValueChange={(v) => setValidationForm(prev => ({ ...prev, global_bonus_type: v }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">€ (fixe)</SelectItem>
                    <SelectItem value="percentage">% (pourcentage)</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={validationForm.global_bonus}
                  onChange={(e) => setValidationForm(prev => ({ ...prev, global_bonus: e.target.value }))}
                  placeholder={validationForm.global_bonus_type === 'fixed' ? "Ex: 50 ou -30" : "Ex: 10 ou -5"}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">
                Valeur positive = bonus, négative = malus.
                {validationForm.global_bonus_type === 'percentage' && ' Sera calculé sur le montant de base.'}
              </p>
            </div>

            {/* Bonus reason */}
            <div className="space-y-2">
              <Label>Justification du bonus/malus</Label>
              <Textarea
                value={validationForm.global_bonus_reason}
                onChange={(e) => setValidationForm(prev => ({ ...prev, global_bonus_reason: e.target.value }))}
                placeholder="Expliquez la raison du bonus ou malus..."
                rows={2}
              />
            </div>

            {/* Agency evaluation */}
            <div className="space-y-2">
              <Label>Évaluation interne (confidentiel)</Label>
              <Textarea
                value={validationForm.agency_evaluation}
                onChange={(e) => setValidationForm(prev => ({ ...prev, agency_evaluation: e.target.value }))}
                placeholder="Votre appréciation générale du travail ce mois..."
                rows={2}
              />
            </div>

            {/* Agency notes */}
            <div className="space-y-2">
              <Label>Notes de validation (visible par le membre)</Label>
              <Textarea
                value={validationForm.agency_notes}
                onChange={(e) => setValidationForm(prev => ({ ...prev, agency_notes: e.target.value }))}
                placeholder="Message au membre..."
                rows={2}
              />
            </div>

            {/* Final amount preview */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>Montant final estimé:</strong>{' '}
                {(() => {
                  const base = selectedReport?.base_amount || 0;
                  const bonus = parseFloat(validationForm.global_bonus) || 0;
                  const amount = validationForm.global_bonus_type === 'percentage' 
                    ? base + (base * bonus / 100)
                    : base + bonus;
                  return <span className="text-lg font-bold">{amount.toFixed(2)}€</span>;
                })()}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleValidate} disabled={validating}>
              {validating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Valider le rapport
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberActivityReport;
